import React from 'react';
import { Loader2, Trash2, Video, Square } from 'lucide-react';
import { ElementsRenderer } from '../../shared';
import { useAuth, useParams, useVideoRecorder, useStableMediaUrl } from '../../../hooks';
import { TEXT_TYPE } from '../../../constants';
import { getMediaBlob } from '../../../utils/mediaSessionCache';
import ReadingComprehensionElement from './ReadingComprehensionElement';
import { Button } from '../../ui/button';
import { cn } from '../../ui/utils';

const VideoRecorderQuestion = ({
  elements,
  settings,
  updateAndSaveAnswer,
  uploadedFile,
  id,
  pauseStrictMode,
  resumeStrictMode,
  readingComprehensionQuestionRef
}) => {
  const { maxDuration = 180, maxFileSize = 50, allowRetakes = true } = settings || {};
  const { studentAssignmentId } = useParams();
  const { token } = useAuth();

  const {
    isRecording,
    isUploading,
    uploadError,
    startError,
    mediaBlob,
    duration,
    startRecording,
    stopRecording,
    retryUpload,
    handleRetake,
    liveVideoRef
  } = useVideoRecorder({
    id,
    maxDuration,
    maxFileSize,
    studentAssignmentId,
    token,
    updateAndSaveAnswer,
    pauseStrictMode,
    resumeStrictMode,
    uploadedFileKey: uploadedFile?.fileKey
  });

  const blob =
    mediaBlob || getMediaBlob({ fileKey: uploadedFile?.fileKey, questionId: id }) || null;
  const { mediaUrl, isLoading: isMediaLoading } = useStableMediaUrl({
    blob,
    fileKey: uploadedFile?.fileKey,
    questionId: id,
    token
  });

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const progressPercent = Math.min(100, (duration / maxDuration) * 100);

  const hasSavedRecording = Boolean(uploadedFile?.fileKey);
  // A recording exists in memory but has not been persisted yet — either the save is still
  // settling or it failed. Without this the panel renders empty and the answer looks lost.
  const hasUnsavedRecording = Boolean(mediaBlob) && !hasSavedRecording && !isUploading;

  return (
    <div className="mt-4 w-full space-y-5">
      <div className="katex-question flex w-full flex-col">
        {readingComprehensionQuestionRef && (
          <ReadingComprehensionElement questionRef={readingComprehensionQuestionRef} />
        )}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-muted-foreground">Video Question</p>
          <div className="question-container text-base leading-relaxed text-foreground md:text-lg">
            <ElementsRenderer elements={elements} textType={TEXT_TYPE.TIPTAP} />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-muted/30 p-4 sm:p-6">
        {(!uploadedFile || !uploadedFile.fileKey) && !mediaBlob && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm font-medium text-muted-foreground">
              Record your answer (Max: {formatTime(maxDuration)})
            </p>

            {isRecording && (
              <div className="w-full max-w-lg overflow-hidden rounded-xl bg-black shadow-md">
                <video
                  ref={liveVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className="mx-auto block max-h-[42vh] w-full object-contain"
                />
              </div>
            )}

            <div className="flex flex-wrap items-center justify-center gap-3">
              {!isRecording ? (
                <Button
                  type="button"
                  size="lg"
                  onClick={startRecording}
                  className="h-12 gap-2 rounded-full px-6 font-semibold"
                >
                  <Video className="h-5 w-5" />
                  Start Video Recording
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    size="lg"
                    onClick={stopRecording}
                    className="h-12 gap-2 rounded-full bg-rose-600 px-6 font-semibold text-white hover:bg-rose-700"
                  >
                    <Square className="h-4 w-4 fill-current" />
                    Stop Recording
                  </Button>
                  <span className="min-w-[4.5rem] text-center text-lg font-bold tabular-nums text-rose-600">
                    {formatTime(duration)}
                  </span>
                </>
              )}
            </div>

            {isRecording && (
              <div className="w-full max-w-lg space-y-2 pb-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      'h-full rounded-full bg-primary transition-[width] duration-300',
                      progressPercent > 90 && 'bg-rose-500'
                    )}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="text-center text-xs text-muted-foreground">
                  {formatTime(duration)} / {formatTime(maxDuration)}
                </p>
              </div>
            )}

            {startError && !isRecording && (
              <div className="w-full max-w-lg rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm font-semibold text-red-700">Recording could not start</p>
                <p className="mt-1 text-sm text-red-700">{startError}</p>
              </div>
            )}
          </div>
        )}

        {isUploading && (
          <div className="flex flex-col items-center gap-3 py-2">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm font-medium text-primary">Uploading your recording...</p>
            <div className="h-2 w-full max-w-lg overflow-hidden rounded-full bg-muted">
              <div className="h-full w-1/2 animate-pulse rounded-full bg-primary/70" />
            </div>
          </div>
        )}

        {hasUnsavedRecording && (
          <div className="flex flex-col items-center gap-4">
            <p
              className={cn(
                'text-sm font-semibold',
                uploadError ? 'text-rose-600' : 'text-muted-foreground'
              )}
            >
              {uploadError ? 'Your recording could not be saved' : 'Finishing up...'}
            </p>

            {uploadError && (
              <p className="max-w-lg text-center text-sm text-muted-foreground">{uploadError}</p>
            )}

            {mediaUrl && (
              <div className="w-full max-w-lg overflow-hidden rounded-xl bg-black shadow-sm">
                <video
                  controls
                  src={mediaUrl}
                  playsInline
                  className="mx-auto block max-h-[42vh] w-full object-contain"
                />
              </div>
            )}

            {uploadError && (
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button type="button" onClick={retryUpload}>
                  Try again
                </Button>
                {allowRetakes && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRetake}
                    className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Discard & Re-record
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {hasSavedRecording && !isUploading && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm font-semibold text-emerald-600">Recording saved successfully!</p>

            <div className="w-full max-w-lg overflow-hidden rounded-xl bg-black shadow-sm">
              {isMediaLoading ? (
                <p className="p-4 text-center text-sm italic text-white/80">Loading recording...</p>
              ) : mediaUrl ? (
                <video
                  controls
                  src={mediaUrl}
                  playsInline
                  className="mx-auto block max-h-[42vh] w-full object-contain"
                />
              ) : (
                <p className="p-4 text-center text-sm italic text-rose-300">Failed to load recording.</p>
              )}
            </div>

            {allowRetakes && (
              <Button
                type="button"
                variant="outline"
                onClick={handleRetake}
                className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="h-4 w-4" />
                Delete & Retake
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoRecorderQuestion;
